import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { errors } from './services/ruutsApi/errors'
import { emitGlobalQueryError } from './utils/Snackbar/globalErrorBridge'

export const REPORT_WINDOW_MS = 5_000

export const createQueryClient = (): QueryClient => {
  // A query that awaits another one (the dynamic-form option getters) rethrows the inner
  // failure, so the same error instance reaches onError twice; it is shown once.
  const reportedErrors = new WeakSet<object>()
  // A focus or reconnect refetch fails every mounted query at once; the user needs one toast, not
  // one per query.
  let lastReported: { message: string; at: number } | null = null
  const isRepeatOfLastReport = (error: unknown, now: number) =>
    error instanceof Error &&
    lastReported?.message === error.message &&
    now - lastReported.at < REPORT_WINDOW_MS
  const reportOnce = (error: unknown) => {
    if (typeof error === 'object' && error !== null) {
      if (reportedErrors.has(error)) return
      reportedErrors.add(error)
    }
    const now = Date.now()
    if (isRepeatOfLastReport(error, now)) return
    if (error instanceof Error) lastReported = { message: error.message, at: now }
    emitGlobalQueryError(error)
  }

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        // On for every query: each screen that seeds local state from a query guards or overlays
        // it (roadmap PR 20 census); a new seeding site needs a guard, not a per-hook opt-out.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // A 4xx is the server's final answer; re-sending it only delays the error.
        retry: (failureCount, error) =>
          !(error instanceof errors.BusinessEntityError) && failureCount < 1,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!query.meta?.suppressGlobalError) reportOnce(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) => {
        if (!mutation.meta?.suppressGlobalError) reportOnce(error)
      },
    }),
  })
}

export const queryClient = createQueryClient()
